import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { authApi } from '../../../api';

const ResetPasswordPage = ({ error = '', message = '' }) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [token, setToken] = useState('');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const RESET_TOKEN_KEY = 'resetToken';
  const RESET_TOKEN_EXP_KEY = 'resetTokenExp';
  const RESET_EMAIL_KEY = 'resetEmail';

  useEffect(() => {
    const tokenFromState = location.state?.token;
    const emailFromState = location.state?.email;
    const tokenFromQuery = searchParams.get('token');
    const storedToken = sessionStorage.getItem(RESET_TOKEN_KEY);
    const storedExp = Number(sessionStorage.getItem(RESET_TOKEN_EXP_KEY) || 0);
    const storedEmail = sessionStorage.getItem(RESET_EMAIL_KEY) || '';

    const now = Date.now();
    const isStoredTokenValid = storedToken && storedExp && storedExp > now;

    const resolvedToken = tokenFromState || tokenFromQuery || (isStoredTokenValid ? storedToken : '');
    const resolvedEmail = emailFromState || storedEmail;

    setToken(resolvedToken);
    setEmail(resolvedEmail);

    if (!resolvedToken) {
      setValidationError('Reset token is missing or expired. Please restart the reset process.');
      sessionStorage.removeItem(RESET_TOKEN_KEY);
      sessionStorage.removeItem(RESET_TOKEN_EXP_KEY);
      sessionStorage.removeItem(RESET_EMAIL_KEY);
    } else if (!isStoredTokenValid && !tokenFromState && !tokenFromQuery) {
      setValidationError('Reset token expired. Please request a new OTP.');
      sessionStorage.removeItem(RESET_TOKEN_KEY);
      sessionStorage.removeItem(RESET_TOKEN_EXP_KEY);
      sessionStorage.removeItem(RESET_EMAIL_KEY);
    } else {
      setValidationError('');
    }
  }, [location.state, searchParams]);

  const enforceMaxLength = (value, maxLength) => {
    if (value.length > maxLength) {
      return value.slice(0, maxLength);
    }
    return value;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidationError('');
    setIsSubmitting(true);

    if (newPassword !== confirmPassword) {
      setValidationError('Passwords do not match');
      setIsSubmitting(false);
      return;
    }

    if (newPassword.length < 8) {
      setValidationError('Password must be at least 8 characters long');
      setIsSubmitting(false);
      return;
    }

    if (!token) {
      setValidationError('Reset token is missing or invalid. Please restart the reset process.');
      setIsSubmitting(false);
      return;
    }

    try {
      const payload = {
        newPassword, 
      };
      await authApi.resetPassword(payload, token);
      sessionStorage.removeItem(RESET_TOKEN_KEY);
      sessionStorage.removeItem(RESET_TOKEN_EXP_KEY);
      sessionStorage.removeItem(RESET_EMAIL_KEY);
      navigate('/login', {
        state: { message: 'Password reset successful. Please sign in.' },
      });
    } catch (err) {
      const errMsg = err?.response?.data?.message || 'Unable to reset password. Please try again.';
      setValidationError(errMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const logoSrc = `${process.env.PUBLIC_URL || ''}/assets/images/chat_logo.png`;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        backgroundImage: `linear-gradient(135deg, rgba(37, 211, 102, 0.65) 0%, rgba(26, 160, 80, 0.7) 50%, rgba(20, 126, 63, 0.75) 100%), url(${process.env.PUBLIC_URL || ''}/assets/images/whatsapp_bg1.jpg)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <Card
        sx={{
          width: '100%',
          maxWidth: 560,
          borderRadius: 4,
          boxShadow: 10,
          backgroundColor: 'rgba(255,255,255,0.92)',
          backdropFilter: 'blur(6px)',
          border: '1px solid rgba(255,255,255,0.4)',
        }}
      >
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Stack spacing={2} alignItems="center">
            <Box sx={{ alignSelf: 'flex-start' }}>
              <Button
                variant="text"
                startIcon={<ArrowLeft size={18} />}
                onClick={() => navigate('/login')}
                sx={{ px: 0, minWidth: 0 }}
              >
                Back
              </Button>
            </Box>
            <Box component="img" src={logoSrc} alt="ChattyBO" sx={{ height: 40 }} />
            <Typography variant="h6">Reset Your Password</Typography>

            {error && error.length > 0 && (
              <Alert severity="error" sx={{ width: '100%' }}>
                {error}
              </Alert>
            )}

            {message && message.length > 0 && (
              <Alert severity="info" sx={{ width: '100%' }}>
                {message}
              </Alert>
            )}

            {validationError && (
              <Alert severity="error" sx={{ width: '100%' }}>
                {validationError}
              </Alert>
            )}
          </Stack>

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
            <input type="hidden" name="token" value={token} />
            <input type="hidden" name="email" value={email} />
            <Stack spacing={2}>
              <TextField
                label="New Password"
                type={showNewPassword ? 'text' : 'password'}
                name="newpassword"
                value={newPassword}
                onChange={(e) => setNewPassword(enforceMaxLength(e.target.value, 20))}
                inputProps={{ minLength: 8, maxLength: 20 }}
                required
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowNewPassword((prev) => !prev)} edge="end" aria-label="toggle new password visibility">
                        {showNewPassword ? <Eye size={16} /> : <EyeOff size={16} />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                label="Confirm Password"
                type={showConfirmPassword ? 'text' : 'password'}
                name="confirmpassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(enforceMaxLength(e.target.value, 20))}
                inputProps={{ minLength: 8, maxLength: 20 }}
                required
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowConfirmPassword((prev) => !prev)} edge="end" aria-label="toggle confirm password visibility">
                        {showConfirmPassword ? <Eye size={16} /> : <EyeOff size={16} />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Stack direction="row" justifyContent="flex-end">
                <Button
                  type="submit"
                  variant="contained"
                  disabled={
                    !newPassword ||
                    !confirmPassword ||
                    newPassword.length < 8 ||
                    confirmPassword.length < 8 ||
                    !token ||
                    !!validationError ||
                    isSubmitting
                  }
                >
                  {isSubmitting ? 'Processing...' : 'Reset Password'}
                </Button>
              </Stack>

              <Typography variant="body2" textAlign="center">
                Remember your password?{' '}
                <Link href="/login" underline="hover" sx={{ color: 'primary.main' }}>
                  Login
                </Link>
              </Typography>
            </Stack>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ResetPasswordPage;
